import { useEffect } from "react";

const LOAD_SELECTOR = "[data-reveal-load]";
const REVEAL_SELECTOR = "[data-reveal]";
const SETTLE_MS = 700;
const STAGGER_MS = 70;
const MAX_DELAY_MS = 280;

/**
 * Runs the page entrance once per load. The load group is CSS-only and already
 * animating at first paint; this hook marks it settled so a later "Edit details"
 * does not replay it, then reveals each scroll target the first time it enters
 * the viewport.
 */
export function usePageMotion() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const loadGroup = Array.from(document.querySelectorAll<HTMLElement>(LOAD_SELECTOR));
    const settle = window.setTimeout(() => {
      loadGroup.forEach((element) => element.classList.add("is-settled"));
    }, SETTLE_MS);

    const elements = Array.from(document.querySelectorAll<HTMLElement>(REVEAL_SELECTOR));
    if (elements.length === 0) {
      return () => window.clearTimeout(settle);
    }

    const reveal = (element: HTMLElement, delay: number) => {
      if (element.classList.contains("is-revealed")) return;
      element.style.setProperty("--reveal-delay", `${delay}ms`);
      element.classList.add("is-revealed");
    };

    if (typeof IntersectionObserver === "undefined") {
      elements.forEach((element) => reveal(element, 0));
      return () => window.clearTimeout(settle);
    }

    const order = new Map<Element, number>(elements.map((element, index) => [element, index]));
    const observer = new IntersectionObserver(
      (entries) => {
        const entering = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => (order.get(a.target) ?? 0) - (order.get(b.target) ?? 0));

        entering.forEach((entry, index) => {
          const element = entry.target as HTMLElement;
          reveal(element, Math.min(index * STAGGER_MS, MAX_DELAY_MS));
          observer.unobserve(element);
        });
      },
      { rootMargin: "0px", threshold: 0.12 },
    );

    elements.forEach((element) => {
      if (!element.classList.contains("is-revealed")) observer.observe(element);
    });

    return () => {
      window.clearTimeout(settle);
      observer.disconnect();
    };
  }, []);
}
